import { PropsWithChildren } from 'react';

import {
  Tooltip,
  Card,
  InfoTooltip,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { getTendencyChange } from '@cast/utils';

import { TendencyIndicator } from 'components/indicators';
import { formatDailyMetricsCost } from 'features/cost-report/utils/metrics/formatDailyMetricsCost';

import { useCostOverTime } from '../../../../cost-report/hooks/useCostOverTime';
import { DailyMetrics } from '../../../../cost-report/types/costOverTime';

type DailyMetricCardProps = {
  title: string;
  titleInfo?: string;
  metrics?: DailyMetrics;
  costStyle?: 'small' | 'medium' | 'large';
  approx?: boolean;
  tendencyHint?: string;
  suffix?: string;
  disableContainer?: boolean;
};

const DailyMetricsCardContainer = ({
  children,
  disable,
}: PropsWithChildren<{ disable?: boolean }>) => {
  if (disable) {
    return children;
  }

  return (
    <Card
      className="tour-highlight"
      sx={{
        p: 16,
      }}
    >
      {children}
    </Card>
  );
};

export const DailyMetricCard = ({
  metrics = {},
  costStyle = 'medium',
  title,
  titleInfo,
  approx,
  tendencyHint,
  suffix,
  disableContainer = false,
}: DailyMetricCardProps) => {
  const { isCollecting } = useCostOverTime();
  const { cost, prevCost } = metrics;
  const tendencyChange =
    !isCollecting && prevCost !== undefined && cost !== undefined
      ? getTendencyChange(cost, prevCost)
      : undefined;

  return (
    <DailyMetricsCardContainer disable={disableContainer}>
      <Stack width="100%" height="100%" gap={12} data-testid="cost-overview">
        <Stack gap={costStyle === 'medium' ? 4 : 8}>
          <Typography variant="L12R" noWrap={true}>
            {title}
            {titleInfo && (
              <InfoTooltip
                noDefaultIconStyling={false}
                title={titleInfo}
                placement="top"
                size="medium"
              />
            )}
          </Typography>
          <Typography
            display="flex"
            alignItems="baseline"
            gap="4px"
            variant={
              costStyle === 'large' && !tendencyChange
                ? 'h2'
                : costStyle === 'medium'
                ? 'h3'
                : 'h5'
            }
            noWrap={true}
          >
            {formatDailyMetricsCost(cost, approx)}
            {suffix && <Typography>/{suffix}</Typography>}
          </Typography>
        </Stack>

        {tendencyChange && (
          <Stack
            data-testid="tendency-indicator"
            alignItems="center"
            direction="row"
            sx={{
              cursor: 'pointer',
              width: 'max-content',
            }}
          >
            <Tooltip
              title={
                tendencyChange.diff
                  ? 'Compared to previous month'
                  : 'No price difference compared to previous month'
              }
              placement="right"
              arrow={true}
            >
              <span>
                <TendencyIndicator
                  variant="arrow"
                  tendency={tendencyChange.tendency}
                  value={formatDailyMetricsCost(tendencyChange.diff)}
                  sx={{ height: 16, display: 'flex' }}
                  size={10}
                  valueProps={{ variant: 'L10M' }}
                />
              </span>
            </Tooltip>
            {tendencyHint && (
              <Typography
                variant="P10R"
                component="span"
                color={tokens.theme.fg.structural.secondary}
                pl={8}
              >
                {tendencyHint}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </DailyMetricsCardContainer>
  );
};
